import React, { useState, useContext, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FaChartBar, FaEye, FaEyeSlash, FaUser, FaEnvelope, FaLock, FaSync, FaShieldAlt } from 'react-icons/fa';
import { FaCow } from "react-icons/fa6";
import { MdMobileFriendly } from "react-icons/md";
import { useTranslation } from 'react-i18next';
import './SignUp.css';
import { Signup } from '../../api/signupApi';
import SocialLogin from '../../SocialLogin/SocialLogin';
import { AuthContext } from '../../Contexts/AuthContext';
import { api } from '../../api';
import logo from "../../screenshots/Milkfarm.jpeg";
export default function SignUp() {
    const { user } = useContext(AuthContext);
    const { t } = useTranslation();
    const [checking, setChecking] = useState(true);
    const [formData, setFormData] = useState({
        nom: '',
        email: '',
        password: '',
        captcha: '',
        captcha_key: ''
    });
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const [captchaLoading, setCaptchaLoading] = useState(false);
    const [captchaUrl, setCaptchaUrl] = useState('');
    const [showPassword, setShowPassword] = useState(false);

    const loadCaptcha = useCallback(async () => {
        try {
            setCaptchaLoading(true);
            const { data } = await axios.get(api + 'captcha');
            const captchaImage = data?.captcha || '';
            const key = data?.key || '';

            setCaptchaUrl(captchaImage);
            setFormData(prev => ({ ...prev, captcha_key: key, captcha: '' }));
        } catch (captchaError) {
            console.error('Captcha load error:', captchaError);
            setError(t('signup.captchaLoadError') || 'Unable to load captcha, please refresh.');
        } finally {
            setCaptchaLoading(false);
        }
    }, [t]);

    useEffect(() => {
        const token = localStorage.getItem("token");
        if (token && user) {
            navigate("/" + user.role);
        } else {
            setChecking(false);
        }
    }, [user, navigate]);

    useEffect(() => {
        loadCaptcha();
    }, [loadCaptcha]);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        if (!formData.captcha.trim()) {
            setError(t('signup.captchaError'));
            return;
        }

        if (!formData.captcha_key) {
            setError(t('signup.captchaLoadError') || 'Unable to load captcha, please refresh.');
            return;
        }

        setLoading(true);

        try {
            const response = await Signup(formData);
            console.log('Successfully registered:', response);
            if (response.token) {
                localStorage.setItem('token', response.token);
            }
            setSuccess(t('signup.successMessage'));
            setFormData(prev => ({ ...prev, captcha: '' }));
            loadCaptcha();
            setTimeout(() => {
                navigate('/verification', {
                    state: { email: formData.email }
                });
            }, 3000);

        } catch (signupError) {
            console.error('Error:', signupError);
            setFormData(prev => ({ ...prev, captcha: '' }));
            loadCaptcha();

            if (signupError.response?.data?.errors) {
                const errors = Object.values(signupError.response.data.errors).flat();
                const firstError = String(errors[0] || '').toLowerCase();

                // Map captcha-related validation messages to translated text
                if (firstError.includes('captcha')) {
                    setError(t('signup.captchaInvalid'));
                // Map email already-exists errors
                } else if (firstError.includes('email') && (firstError.includes('already') || firstError.includes('existe') || firstError.includes('déjà'))) {
                    setError(t('signup.emailExists'));
                } else {
                    setError(errors.join(', '));
                }
            } else if (signupError.response?.data?.message) {
                const rawMessage = String(signupError.response.data.message);
                const msg = rawMessage.toLowerCase();
                if (msg.includes('captcha')) {
                    setError(t('signup.captchaInvalid'));
                } else if (msg.includes('email') && (msg.includes('already') || msg.includes('existe') || msg.includes('déjà'))) {
                    setError(t('signup.emailExists'));
                } else {
                    setError(rawMessage);
                }
            } else {
                setError(t('signup.captchaLoadError') || 'Un problème est survenu, merci de réessayer');
            }
        } finally {
            setLoading(false);
        }
    };

    if (checking) return null;

    return (
        <div className="signup-page">
            <div className="signup-left">
                <div className="signup-content">
                    <div className="logo-section">
                        <div className="logo-icon">
                            <img src={logo} alt="MilkFarm Logo" className="logo-image" />
                        </div>
                        <h1 className="logo-text">{t('signup.headerTitle')}</h1>
                    </div>

                    <div className="welcome-text">
                        <h2>{t('signup.title')}</h2>
                        <p>{t('signup.subtitle')}</p>
                    </div>

                    {error && (
                        <div className="error-message">
                            <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                            </svg>
                            {error}
                        </div>
                    )}

                    {success && (
                        <div className="success-message">
                            <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                            {success}
                        </div>
                    )}

                    <SocialLogin />

                    <form onSubmit={handleSubmit} className="signup-form">
                        <div className="input-group">
                            <label htmlFor="nom">{t('signup.fields.nom')}</label>
                            <div className="input-wrapper">
                                <FaUser className="input-icon" />
                                <input
                                    id="nom"
                                    className="input-field"
                                    type="text"
                                    name="nom"
                                    placeholder={t('signup.placeholders.nom')}
                                    value={formData.nom}
                                    onChange={handleChange}
                                    required
                                />
                            </div>
                        </div>

                        <div className="input-group">
                            <label htmlFor="email">{t('signup.fields.email')}</label>
                            <div className="input-wrapper">
                                <FaEnvelope className="input-icon" />
                                <input
                                    id="email"
                                    className="input-field"
                                    type="email"
                                    name="email"
                                    placeholder={t('signup.placeholders.email')}
                                    value={formData.email}
                                    onChange={handleChange}
                                    required
                                />
                            </div>
                        </div>

                        <div className="input-group">
                            <label htmlFor="password">{t('signup.fields.password')}</label>
                            <div className="input-wrapper">
                                <FaLock className="input-icon" />
                                <input
                                    id="password"
                                    className="input-field"
                                    type={showPassword ? 'text' : 'password'}
                                    name="password"
                                    placeholder={t('signup.placeholders.password')}
                                    value={formData.password}
                                    onChange={handleChange}
                                    required
                                    minLength={8}
                                />
                                <button
                                    type="button"
                                    className="input-toggle"
                                    onClick={() => setShowPassword((value) => !value)}
                                    aria-label={showPassword ? t('profile.hidePassword') : t('profile.showPassword')}
                                >
                                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                                </button>
                            </div>
                        </div>

                        <div className="captcha-group">
                            <label htmlFor="captcha">{t('signup.fields.captcha') || 'Security code'}</label>
                            <div className="captcha-row">
                                <div className="captcha-visual" aria-live="polite">
                                    {captchaUrl ? (
                                        <img src={captchaUrl} alt={t('signup.captcha.alt') || 'Captcha challenge'} />
                                    ) : (
                                        <span className="captcha-placeholder">{t('signup.captcha.loading') || 'Loading captcha...'}</span>
                                    )}
                                </div>
                                <button
                                    type="button"
                                    className="captcha-refresh"
                                    onClick={loadCaptcha}
                                    disabled={captchaLoading}
                                    aria-label={t('signup.captcha.refresh') || 'Refresh captcha'}
                                >
                                    <FaSync />
                                </button>
                            </div>
                            <input
                                id="captcha"
                                name="captcha"
                                className="input-field captcha-input"
                                type="text"
                                value={formData.captcha}
                                onChange={handleChange}
                                placeholder={t('signup.placeholders.captcha') || 'Enter the characters'}
                                autoComplete="off"
                                required
                            />
                        </div>

                        <button
                            className="submit-button"
                            type="submit"
                            disabled={loading}
                        >
                            {loading ? (
                                <>
                                    <svg className="spinner" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                        <circle cx="10" cy="10" r="8" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeDasharray="50.265" strokeDashoffset="25" />
                                    </svg>
                                    {t('signup.creating')}
                                </>
                            ) : (
                                <>
                                    {t('signup.createAccount')}
                                    <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z" clipRule="evenodd" />
                                    </svg>
                                </>
                            )}
                        </button>

                        <div className="form-footer">
                            <p>
                                {t('signup.haveAccount')}{' '}
                                <a href="/signin" className="link-primary">{t('signup.login')}</a>
                            </p>
                        </div>
                    </form>
                </div>
            </div>

            <div className="signup-right">
                <div className="hero-content">
                    <div className="hero-badge">
                        <span className="badge-dot"></span>
                        {t('signup.hero.badge')}
                    </div>

                    <h1 className="hero-title">
                        {t('signup.hero.title')}
                        <span className="gradient-text">{t('signup.hero.titleHighlight')}</span>
                    </h1>

                    <p className="hero-description">
                        {t('signup.hero.description')}
                    </p>

                    <div className="features-grid">
                        <div className="feature-card">
                            <div className="feature-icon"><FaChartBar /></div>
                            <h3>{t('signup.hero.stats')}</h3>
                            <p>{t('signup.hero.statsDesc')}</p>
                        </div>

                        <div className="feature-card">
                            <div className="feature-icon"><MdMobileFriendly /></div>
                            <h3>{t('signup.hero.mobile')}</h3>
                            <p>{t('signup.hero.mobileDesc')}</p>
                        </div>

                        <div className="feature-card">
                            <div className="feature-icon"><FaShieldAlt /></div>
                            <h3>{t('signup.hero.security') || 'Sécurisé'}</h3>
                            <p>{t('signup.hero.securityDesc') || 'Vos données sont protégées et chiffrées'}</p>
                        </div>

                        <div className="feature-card">
                            <div className="feature-icon"><FaCow /></div>
                            <h3>{t('signup.hero.herd') || 'Gestion du troupeau'}</h3>
                            <p>{t('signup.hero.herdDesc') || 'Suivez chaque animal en détail'}</p>
                        </div>
                    </div>
                </div>

                <div className="bg-decoration decoration-1"></div>
                <div className="bg-decoration decoration-2"></div>
                <div className="bg-decoration decoration-3"></div>
                <div className="bg-grid"></div>
            </div>
        </div>
    );
}